import { createSlice } from '@reduxjs/toolkit';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { environmentURLs } from '../../../components/common/CaptureEnvironment';
import { DMS_ACCOUNT_NAME_TYPE_MAPPING } from '../../../components/stepper/stepperBody/stepperBodyComponents/DMSSelectionComponents/utils/constant';
import { client } from '../../../utils/apolloClient';
import { requiredApiFieldsForSpec } from '../../../constants/constants';

const REQUESTED_STATUS_TO_STATUS_MAPPING = {
  enable: 'enabled',
  disable: 'disabled',
};

const initialState = {
  disableNext: true,
  requestedIntegrationStatuses: {},
  requestedCppStatuses: {},
  dbIntegrationStatuses: {},
  requestedAPIFields: [],
  apiFields: [],
  apiFieldSearchInput: '',
  apiRequiredFields: [],
  requestedApiSubscriptionSettings: {},
  isEISDMSInfoPending: false,
  isEISDMSInfoSuccess: false,
  isEISDMSInfoLoading: false,
  errorInGetEISDMSInfo: null,
  dmsLocationSpecificAccounts: [],
  dmsLocations: [],
  dmsDepartments: [],
  consentCompletedLocations: [],
  isConsentSaveSuccess: false,
  updateCppPending: false,
  updateCppError: null,
  updateCppSuccess: false,
};

const checkDisableNext = (dmsLocationSpecificAccounts) => {
  let disable = false;
  dmsLocationSpecificAccounts.forEach((location) => {
    location.departments.forEach(dep => {
      if (!dep.accounts.some(a => a.isVisited)) {
        disable = true;
      }
    })
  })
  return disable;
}

export const getDMSAccountDetails = createAsyncThunk(
  'getDMSAccountDetails',
  async ({ cmfNumber, storeId, token, location, entityId, dmsApiDetails }) => {
    let response = await client.authorizeRequest({
      method: 'GET',
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/v1/connectCDK/${storeId}`,
      headers:{
        Authorization : `Bearer ${token}`
      }
    });
    return {
      data: response.data,
      location,
      cmfNumber,
      storeId,
      entityId,
      dmsApiDetails
    };
  }
);

export const saveApiConsentForSelectedLocations = createAsyncThunk(
  'saveApiConsentForSelectedLocations',
  async ({ solution, consent, token, locations }) => {
    const { id: solutionId, listingVersion = '1' } = solution;
    let response = await client.authorizeRequest({
      method: 'POST',
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/solutions/${solutionId}/${listingVersion}/consents/multiple`,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      data: {
        consent: consent,
      },
    });
    return {
      data: response.data,
      locations,
    };
  }
);

export const updateCppForAppV2 = createAsyncThunk(
  'updateCppForAppV2',
  async ({ appId, optOuts, token }) => {
    const { data } = await client.authorizeRequest({
      method: 'PATCH',
      url: `${environmentURLs.REACT_APP_MP_DP_METADATA_URL}/privacy-protection`,
      data: { appId, optOuts },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    return { data };
  }
)

export const ManageIntegrationsV2 = createSlice({
  name: 'ManageIntegrationsV2',
  initialState,
  reducers: {
    disableNext: (state, action) => {
      state.disableNext = action.payload
    },
    storeDBIntegrationStatuses: (state, action) => {
      state.dbIntegrationStatuses = action.payload;
    },
    resetState: () => initialState,
    resetConsentStatus: (state, action) => {
      state.isConsentSaveSuccess = false;
    },
    requestIntegrationStatusChange: (state, action) => {
      const {entityId, requestedStatus} = action.payload;
      if(REQUESTED_STATUS_TO_STATUS_MAPPING[requestedStatus] !== state.dbIntegrationStatuses[entityId]?.status) {
        state.requestedIntegrationStatuses = {
          ...state.requestedIntegrationStatuses,
          [entityId]: requestedStatus
        }
      }
      else {
        const requestedChanges = {...state.requestedIntegrationStatuses};
        delete requestedChanges[entityId];
        state.requestedIntegrationStatuses = requestedChanges;
      }
    },
    requestedIntegrationCppStatusChange: (state, action) => {
      const { entityId, isChecked } = action.payload;
      state.requestedCppStatuses = {
        ...state.requestedCppStatuses,
        [entityId]: isChecked,
      };
    },
    selectAllLocationsForCpp: (state, action) => {
      const { allLocations } = action.payload;
      let allLocationsForCpp;
      allLocations.forEach((f) => {
        const entityId = f.entityId;
        allLocationsForCpp = { ...allLocationsForCpp, [entityId]: true };
      });
      state.requestedCppStatuses = allLocationsForCpp;
    },
    setApiSubscriptionSettings: (state, action) => {
      const { entityId, settings } = action.payload;
      if (!state.requestedApiSubscriptionSettings[entityId]) {
        state.requestedApiSubscriptionSettings[entityId] = {};
      }
      for (const [key, value] of Object.entries(settings || {})) {
        state.requestedApiSubscriptionSettings[entityId][key] = value;
      }
    },
    selectedApiFields: (state, action) => {
      const { fieldName, isSelected } = action.payload;
      if (!isSelected) {
        let apiFieldsArray = [...state.requestedAPIFields];
        apiFieldsArray = apiFieldsArray.filter((f) => f !== fieldName);
        state.requestedAPIFields = apiFieldsArray;
        apiFieldsArray = apiFieldsArray.filter(
          (f) => !requiredApiFieldsForSpec.has(f)
        );
        if(!apiFieldsArray.length) {
          state.disableNext = true;
        }
      } else {
        const apiFieldsArray = [...state.requestedAPIFields, fieldName];
        state.requestedAPIFields = apiFieldsArray;
        state.disableNext = false;
      }
    },
    selectedAllApiFields: (state, action) => {
      const { fields, operation } = action.payload;
      if (operation === 'addAllFields') {
        state.requestedAPIFields = fields;
        state.disableNext = false;
      } 
      if (operation === 'removeAllFields') {
        state.requestedAPIFields = fields;
        if (fields.length === 0) {
          state.disableNext = true;
        }
      }
    },
    setApiFieldSearchInput: (state, action) => {
      state.apiFieldSearchInput = action.payload;
    },
    storeAllApiFields: (state, action) => {
      let apiFieldsArray = [];
      let allApiFields = [];
      let apiRequiredFields = [];
      const { apiFields, manualSelectionFields } = action.payload;
      
      const manualSelectionFieldsSet = Array.isArray(manualSelectionFields) && 
          manualSelectionFields.length > 0 
          ? new Set(manualSelectionFields) 
          : null;

      apiFields.forEach((f) => {
        allApiFields.push(f.name);
        // Only include field if it's not in manualSelectionFields
        if (!manualSelectionFieldsSet?.has(f.name)) {
          apiFieldsArray.push(f.name);
        }
        if(f.required) {
          apiRequiredFields.push(f.name);
        }
      });
      
      state.requestedAPIFields = apiFieldsArray;
      state.apiFields = allApiFields;
      state.apiRequiredFields = apiRequiredFields;
    },
    clearDMSAccountInfo: (state, action) => {
      state.isEISDMSInfoPending = false;
      state.isEISDMSInfoSuccess = false;
      state.isEISDMSInfoLoading = false;
      state.errorInGetEISDMSInfo = null;
    },
    setDmsLocations: (state, action) => {
        state.dmsLocations = action.payload
    },
    setDmsDepartments: (state, action) => {
      state.dmsDepartments = action.payload
    },
    handleLocationSelection: (state, action) => {
      let userSelectedLocation = action.payload;
      let locData = state.dmsLocationSpecificAccounts.map(e => {
        if(e.entityId === userSelectedLocation.entityId){
          return {
            ...e,
            isVisited: true
          }
        } else {
          return {
            ...e,
            isVisited: false
          }
        }
      })
      state.dmsLocationSpecificAccounts = locData;
      state.disableNext = checkDisableNext(locData);
    },
    handleDepartmentSelection: (state, action) => {
      let locationData = state.dmsLocationSpecificAccounts.map((location) => {
        if(location.isVisited){
          let dep = location.departments.map(e => {
            if(action.payload.name === e.name){
              return {
                ...e,
                isVisited: true
              }
            } else {
              return {
                ...e,
                isVisited: false
              }
            }
          })
          return {
            ...location,
            departments: dep
          }
        } else {
          return {
            ...location
          }
        }
      });
      state.dmsLocationSpecificAccounts = locationData;
      state.disableNext = checkDisableNext(locationData);
    },
    handleDMSAccountSelection: (state, action) => {
      let {check, event} = action.payload
      let locationData = state.dmsLocationSpecificAccounts.map((location) => {
        if(location.isVisited){
          let depData = location.departments.map(department => {
            if(department.isVisited){
              let accountData = department.accounts.map(a => {
                if(a.name === event.name){
                  return {
                    ...a,
                    isVisited: check
                  }
                } else {
                  return {
                    ...a
                  }
                }
              })
              let selectAllValue = accountData.some(account => account.isVisited === false);
              return {
                ...department,
                accounts: accountData,
                isSelectAll: !selectAllValue
              };
            } else {
              return {
                ...department
              }
            }
          })
          return {
            ...location,
            departments: depData
          }
        } else {
          return {
            ...location
          }
        }
      });
      state.dmsLocationSpecificAccounts = locationData;
      state.disableNext = checkDisableNext(locationData);
    },
    handleDMSAccountSelectionAll: (state, action) => {
      let check = action.payload
      let locationData = state.dmsLocationSpecificAccounts.map((location) => {
        if(location.isVisited){
          let depData = location.departments.map(department => {
            if(department.isVisited){
              let accountData = department.accounts.map(a => {
                if(check){
                  return {
                    ...a,
                    isVisited: true
                  }
                }
                if(!check) {
                  return {
                    ...a,
                    isVisited: false
                  }
                }
              })
              return {
                ...department,
                accounts: accountData,
                isSelectAll: check
              };
            } else {
              return {
                ...department
              }
            }
          })
          return {
            ...location,
            departments: depData
          }
        } else {
          return {
            ...location
          }
        }
      });
      state.dmsLocationSpecificAccounts = locationData;
      state.disableNext = checkDisableNext(locationData);
    },
    handleRemoveLocation: (state, action) => {
      let removedSelectedLocationData = action.payload;
      let locationData = state.dmsLocationSpecificAccounts.filter((f) => !removedSelectedLocationData.find(e => e.cmfNumber === f.cmfNumber))
      state.dmsLocationSpecificAccounts = locationData;
      state.disableNext = checkDisableNext(locationData);
    }
  },

  extraReducers: {
    [getDMSAccountDetails.fulfilled]: (state, action) => {
      const {
        data,
        location,
        cmfNumber,
        entityId,
      } = action.payload;
      let accounts = data?.departments?.map((dep) => {
        return {
            id: dep.id,
            name: dep.name,
            status: dep.status,
            type: dep.type,
            isVisited: false
        };
      });
      let departments = [];
      state.dmsDepartments.forEach((dep, ind) => {
        let obj = {
          name: dep.name,
          type: DMS_ACCOUNT_NAME_TYPE_MAPPING[dep.name],
          accounts,
          isVisited: false,
          isSelectAll: false
        }
        if (ind === 0) {
          obj.isVisited = true
        }
        departments.push(obj);
      });
      const dmsObj = {
        location,
        cmfNumber,
        entityId,
        departments,
        isVisited: false
      }
      const combineData = [...state.dmsLocationSpecificAccounts, dmsObj]
      const uniqueLocations = [
        ...new Map(
          combineData.map((item) => [item.entityId, item])
        ).values(),
      ];
      uniqueLocations[0].isVisited = true;
      state.dmsLocationSpecificAccounts = uniqueLocations
      state.disableNext = checkDisableNext(uniqueLocations);
      state.isEISDMSInfoSuccess = true;
      state.isEISDMSInfoPending = false;
      state.isEISDMSInfoLoading = false;
    },
    [getDMSAccountDetails.pending]: (state, action) => {
      state.isEISDMSInfoPending = true;
      state.isEISDMSInfoLoading = true;
    },
    [getDMSAccountDetails.rejected]: (state, action) => {
      state.isEISDMSInfoSuccess = false;
      state.isEISDMSInfoPending = true;
      state.isEISDMSInfoLoading = false;
      state.errorInGetEISDMSInfo = action.error;
    },
    [saveApiConsentForSelectedLocations.fulfilled]: (state, action) => {
      const { locations } = action.payload;
      const locationsList = locations?.map(
        (f) => f.entityId,
        ...state.consentCompletedLocations
      );
      state.consentCompletedLocations = locationsList;
      state.isConsentSaveSuccess = true;
    },
    [saveApiConsentForSelectedLocations.pending]: (state, action) => {
      state.isConsentSaveSuccess = false;
    },
    [saveApiConsentForSelectedLocations.rejected]: (state, action) => {
      state.isConsentSaveSuccess = false;
    },
    [updateCppForAppV2.pending]: state => {
      state.updateCppPending = true;
      state.updateCppError = null;
      state.updateCppSuccess = false;
    },
    [updateCppForAppV2.fulfilled]: state => {
      state.updateCppPending = false;
      state.updateCppSuccess = true;
    },
    [updateCppForAppV2.rejected]: (state, { error }) => {
      state.updateCppPending = false;
      state.updateCppError = error;
      state.updateCppSuccess = false;
    },
  }
});

export const {
  disableNext,
  storeDBIntegrationStatuses,
  resetState,
  requestIntegrationStatusChange,
  requestedIntegrationCppStatusChange,
  selectAllLocationsForCpp,
  setApiSubscriptionSettings,
  setApiFieldSearchInput,
  selectedApiFields,
  storeAllApiFields,
  selectedAllApiFields,
  storeUncheckedRequiredFields,
  clearDMSAccountInfo,
  setDmsLocations,
  setDmsDepartments,
  handleLocationSelection,
  handleDepartmentSelection,
  handleDMSAccountSelection,
  handleDMSAccountSelectionAll,
  handleRemoveLocation,
  resetConsentStatus
} = ManageIntegrationsV2.actions;

export default ManageIntegrationsV2.reducer;
